const ACTIVE_CLASS = "rupee-animation-active";
const CONTAINER_CLASS = "rupee-notes-container";

export function initRupeeAnimation(containerElement) {
  // =========================
  // SAFETY CHECKS
  // =========================
  if (typeof window === "undefined") return () => {};
  if (!containerElement) return () => {};

  // Prevent duplicate initialization
  if (containerElement.classList.contains(ACTIVE_CLASS)) {
    return () => {};
  }

  containerElement.classList.add(ACTIVE_CLASS);

  let destroyed = false;

  const timeouts = new Set();
  const rafs = new Set();
  const createdNodes = new Set();

  // =========================
  // HELPERS
  // =========================

  const safeTimeout = (callback, delay = 0) => {
    const id = setTimeout(() => {
      timeouts.delete(id);

      if (!destroyed) {
        callback();
      }
    }, delay);

    timeouts.add(id);

    return id;
  };

  const safeRAF = (callback) => {
    const id = requestAnimationFrame(() => {
      rafs.delete(id);

      if (!destroyed) {
        callback();
      }
    });

    rafs.add(id);

    return id;
  };

  const safeCreate = (tag, className = "") => {
    const el = document.createElement(tag);

    if (className) {
      el.className = className;
    }

    createdNodes.add(el);

    return el;
  };

  const safeAppend = (parent, child) => {
    if (
      !destroyed &&
      parent &&
      child &&
      parent.isConnected &&
      !child.isConnected
    ) {
      parent.appendChild(child);
    }
  };

  const safeRemove = (node) => {
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }

    createdNodes.delete(node);
  };

  // =========================
  // CREATE CONTAINER
  // =========================

  let notesContainer = containerElement.querySelector(`.${CONTAINER_CLASS}`);

  if (!notesContainer) {
    notesContainer = safeCreate("div", CONTAINER_CLASS);

    safeAppend(containerElement, notesContainer);
  }

  // =========================
  // NOTE DECORATION
  // =========================

  const addNoteDesign = (note) => {
    const elements = [
      "security-thread",
      "mahatma-portrait",
      "ashoka-chakra",
      "watermark",
    ];

    elements.forEach((className) => {
      const el = safeCreate("div", className);

      note.appendChild(el);
    });
  };

  // =========================
  // EXPLOSION PARTICLES
  // =========================

  const createExplosion = ({ noteType, x, y, count = 60 }) => {
    if (destroyed) return;

    const fragment = document.createDocumentFragment();

    for (let i = 0; i < count; i++) {
      const piece = safeCreate("div", "rupee-note rupee-piece");

      piece.dataset.noteType = noteType;

      const size = 24 + Math.random() * 36;

      const angle = Math.random() * Math.PI * 2;

      const velocity = 220 + Math.random() * 320;

      const moveX = Math.cos(angle) * velocity;

      const moveY = Math.sin(angle) * velocity - 180;

      const rotation = Math.random() * 1080 - 540;

      piece.style.cssText = `
        left:${x}%;
        top:${y}px;
        width:${size}px;
        height:${size * 0.48}px;
        opacity:${0.75 + Math.random() * 0.25};
        --scatter-x:${moveX}px;
        --scatter-y:${moveY}px;
        --scatter-rot:${rotation}deg;
      `;

      addNoteDesign(piece);

      fragment.appendChild(piece);

      safeTimeout(() => {
        safeRemove(piece);
      }, 2400);
    }

    safeAppend(notesContainer, fragment);
  };

  // =========================
  // BIG EXPLODING NOTE
  // =========================

  const createExplodingNote = ({
    noteType,
    delay = 0,
    position = "center",
  }) => {
    safeTimeout(() => {
      if (destroyed) return;

      const note = safeCreate("div", "rupee-note exploding-note");

      note.dataset.noteType = noteType;

      note.style.cssText = `
        width:150px;
        height:72px;
        opacity:1;
        top:-220px;
        z-index:12;
        will-change:transform, top;
        backface-visibility:hidden;
        transform-style:preserve-3d;
      `;

      if (position === "left") {
        note.style.left = "24%";
      } else if (position === "right") {
        note.style.left = "76%";
      } else {
        note.style.left = "50%";
      }

      note.style.transform = "translate3d(-50%,0,0) rotate(0deg)";

      addNoteDesign(note);

      safeAppend(notesContainer, note);

      // Delay for smooth paint
      safeRAF(() => {
        safeRAF(() => {
          if (!note.isConnected) return;

          note.style.transition =
            "transform 3.8s cubic-bezier(.22,.61,.36,1), top 3.8s cubic-bezier(.22,.61,.36,1)";

          note.style.top = position === "center" ? "45vh" : "70vh";

          note.style.transform = "translate3d(-50%,0,0) rotate(240deg)";
        });
      });

      // Explosion
      safeTimeout(() => {
        if (!note.isConnected) return;

        const rect = note.getBoundingClientRect();

        const parentRect = notesContainer.getBoundingClientRect();

        const centerX =
          ((rect.left + rect.width / 2 - parentRect.left) / parentRect.width) *
          100;

        const centerY = rect.top - parentRect.top + rect.height / 2;

        createExplosion({
          noteType,
          x: centerX,
          y: centerY,
        });

        safeRemove(note);
      }, 3900);
    }, delay);
  };

  // =========================
  // FLOATING NOTES
  // =========================

  const createFloatingNotes = () => {
    if (destroyed) return;

    const fragment = document.createDocumentFragment();

    const noteCount = window.innerWidth < 768 ? 18 : 36;

    for (let i = 0; i < noteCount; i++) {
      const note = safeCreate("div", "rupee-note floating-note");

      const is500 = Math.random() > 0.4;

      const noteType = is500 ? "500" : "200";

      note.dataset.noteType = noteType;

      const width = is500 ? 70 + Math.random() * 28 : 58 + Math.random() * 22;

      const duration = 14 + Math.random() * 10;

      const delay = Math.random() * 8;

      const drift = Math.random() * 120 - 60;

      note.style.cssText = `
        left:${Math.random() * 100}%;
        width:${width}px;
        height:${width * 0.48}px;
        opacity:${0.45 + Math.random() * 0.4};
        animation-duration:${duration}s;
        animation-delay:${delay}s;
        --drift:${drift}px;
        will-change:transform;
        backface-visibility:hidden;
      `;

      addNoteDesign(note);

      fragment.appendChild(note);
    }

    safeAppend(notesContainer, fragment);
  };

  // =========================
  // START ANIMATION
  // =========================

  safeTimeout(() => {
    createExplodingNote({
      noteType: "200",
      delay: 0,
      position: "left",
    });

    createExplodingNote({
      noteType: "500",
      delay: 1100,
      position: "right",
    });
  }, 250);

  safeTimeout(() => {
    createFloatingNotes();
  }, 6200);

  // =========================
  // CLEANUP
  // =========================

  return () => {
    destroyed = true;

    containerElement.classList.remove(ACTIVE_CLASS);

    timeouts.forEach((id) => clearTimeout(id));

    rafs.forEach((id) => cancelAnimationFrame(id));

    createdNodes.forEach((node) => {
      safeRemove(node);
    });

    if (notesContainer) {
      safeRemove(notesContainer);
    }

    timeouts.clear();
    rafs.clear();
    createdNodes.clear();
  };
}
